import { useState } from "react";
import { Link } from "react-router-dom";
import { getGrowthSummary } from "@/lib/growth-summary";
import { LazySection } from "@/hooks/useLazySection";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import {
  Award,
  
  Clock,
  PiggyBank,
  TrendingUp,
  Heart,
  TreePine,
  AlertTriangle,
  Globe,
  Target,
  CheckCircle2,
} from "lucide-react";
import heroImageWebp from "@/assets/hero-warehouse.webp";
import heroSrcset from "@/assets/hero-warehouse.jpg?w=640;960;1280;1920&format=webp&as=srcset";
import heroVideo from "@/assets/hero-warehouse-video.mp4";
import heroAktionsversand from "@/assets/hero-aktionsversand.jpg";
import heroRetouren from "@/assets/hero-retouren.jpg";
import heroLohnverpackung from "@/assets/hero-lohnverpackung.jpg";
import heroPreFba from "@/assets/hero-pre-fba.jpg";
import heroSfp from "@/assets/hero-seller-fulfilled-prime.jpg";
import heroDashboard from "@/assets/hero-dashboard.jpg";
import heroGreenmoose from "@/assets/hero-greenmoose.jpg";
import { AnimatedHeroBackground } from "@/components/home/AnimatedHeroBackground";
import { ServiceCard } from "@/components/home/ServiceCard";
import { SEOHead, localBusinessJsonLd, websiteJsonLd, organizationJsonLd } from "@/components/SEOHead";
import { GrowthChart } from "@/components/home/GrowthChart";
import { KineticWord } from "@/components/home/KineticWord";
import { AnimatedUnderline } from "@/components/home/AnimatedUnderline";
import { LatestBlogPosts } from "@/components/home/LatestBlogPosts";
import { useScrollUnderline } from "@/hooks/useScrollUnderline";
import { useParallax } from "@/hooks/useParallax";

const services = [
  {
    imageUrl: heroImageWebp,
    title: "Fulfillment",
    imageAlt: "Mitarbeiter beim Kommissionieren und Verpacken von E-Commerce-Bestellungen im emoose Lager",
    description:
      "Endlich wieder Zeit für dein Business – statt für Kartons und Versandetiketten.",
    href: "/fulfillment",
  },
  {
    imageUrl: heroPreFba,
    title: "Pre-FBA Service",
    imageAlt: "Gelabelte Kartons auf Paletten bereit für den Amazon FBA Versand",
    description:
      "Amazon ohne Stress – wir machen deine Ware FBA-ready, du bleibst fokussiert.",
    href: "/pre-fba",
  },
  {
    imageUrl: heroSfp,
    title: "Seller Fulfilled Prime",
    imageAlt: "Paket wird mit Scanner am Versandtisch für Amazon Prime verarbeitet",
    description:
      "Prime-Badge verdienen, ohne dich um Lieferzeiten zu sorgen.",
    href: "/seller-fulfilled-prime",
  },
  {
    imageUrl: heroLohnverpackung,
    title: "Lohnverpackung",
    imageAlt: "Identische Produkte werden am Fließband professionell verpackt und konfektioniert",
    description:
      "Deine Produkte, perfekt in Szene gesetzt – ohne dass du Hand anlegen musst.",
    href: "/lohnverpackung",
  },
  {
    imageUrl: heroAktionsversand,
    title: "Aktionsversand",
    imageAlt: "Großvolumiger Versand von Marketing-Aktionspaketen im Fulfillment-Lager",
    description:
      "Black Friday? Entspann dich – wir skalieren mit, während du schläfst.",
    href: "/aktionsversand",
  },
  {
    imageUrl: heroRetouren,
    title: "Retouren",
    imageAlt: "Retournierte Pakete werden digital geprüft und für die Wiedereinlagerung vorbereitet",
    description:
      "Retouren nerven? Nicht mehr dein Problem.",
    href: "/retouren",
  },
  {
    imageUrl: heroGreenmoose,
    title: "greenmoose",
    imageAlt: "Plastikfreie und nachhaltige Verpackungsmaterialien im emoose Lager",
    description:
      "Gutes Gewissen inklusive – deine Kunden lieben es, du auch.",
    href: "/greenmoose",
  },
  {
    imageUrl: heroDashboard,
    title: "Fulfillment Dashboard",
    imageAlt: "Fulfillment-Dashboard mit Echtzeit-Übersicht über Bestände und Versandstatus",
    description:
      "Volle Kontrolle, null Stress – sieh alles auf einen Blick.",
    href: "/fulfillment-dashboard",
  },
];

function SectionFallback() {
  return <div className="min-h-[200px]" />;
}

export default function Index() {
  const [growthSummary, setGrowthSummary] = useState<ReturnType<typeof getGrowthSummary>>(null);
  const parallaxOffset = useParallax(0.15);
  const servicesUnderline = useScrollUnderline<HTMLHeadingElement>();
  const growthUnderline = useScrollUnderline<HTMLHeadingElement>();
  const reasonsUnderline = useScrollUnderline<HTMLHeadingElement>();
  
  return (
    <Layout>
      <SEOHead
        title="E-Commerce Fulfillment ohne Mindestmengen | emoose"
        description="Professionelles E-Commerce Fulfillment: Lagerung, Same-Day-Versand mit DHL und Retourenmanagement. Flexibel, transparent und ohne Mindestmengen."
        path="/"
        jsonLd={[localBusinessJsonLd, websiteJsonLd]}
      />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-[90vh] lg:min-h-[95vh] flex items-center overflow-hidden bg-foreground -mt-[72px]">
        <div className="absolute inset-0 scale-105">
          {/* Video background – hidden on mobile for performance */}
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster={heroImageWebp}
            className="hidden sm:block w-full h-full object-cover"
          >
            <source src={heroVideo} type="video/mp4" />
          </video>
          {/* Static image fallback for mobile */}
          <picture className="sm:hidden">
            <source
              srcSet={heroSrcset}
              type="image/webp"
              sizes="100vw"
            />
            <img
              src={heroImageWebp}
              alt="emoose Fulfillment Lagerhalle"
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="async"
              width={1920}
              height={1080}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-br from-foreground/95 via-foreground/88 to-foreground/75" />
        </div>

        {/* Bottom fade gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-foreground/40 to-transparent z-[4]" />

        <div style={{ transform: `translateY(${parallaxOffset}px)` }}>
          <AnimatedHeroBackground />
        </div>

        <div className="container-section relative z-10 pt-24 sm:pt-28 pb-16 sm:pb-24 lg:pb-32">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-background mb-6 sm:mb-8 animate-fade-up leading-tight">
              Dein Lager ist voll.
              <br />
              Dein Kopf auch?
              <span className="text-primary-on-dark block mt-2">
                <KineticWord delay={800}>Wir übernehmen.</KineticWord>
              </span>
            </h1>

            <p className="text-lg sm:text-xl lg:text-2xl text-background/80 mb-10 sm:mb-12 animate-fade-up animation-delay-100 max-w-2xl mx-auto leading-relaxed">
              Fulfillment mit Händler-DNA: Wir schaffen die Klarheit in deiner Logistik, die du für echtes Wachstum brauchst.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 animate-fade-up animation-delay-200 justify-center">
              <Link to="/anfrage">
                <Button variant="hero" size="xl" className="w-full sm:w-auto">
                  Anfrage starten
                </Button>
              </Link>
              <Link to="/preise">
                <Button variant="heroOutline" size="xl" className="w-full sm:w-auto">
                  Preise ansehen
                </Button>
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* Curved transition – outside hero to avoid overflow/positioning issues */}
      <div className="relative -mt-px bg-foreground">
        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" preserveAspectRatio="none">
          <path d="M0 40V20C240 5 480 0 720 0C960 0 1200 5 1440 20V40H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>

      {/* 7 Services Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-section">
        <div className="container-section">
          <div className="text-center max-w-5xl mx-auto mb-8 sm:mb-12">
            <h2 ref={servicesUnderline.ref} className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 sm:mb-8">
            Ein starkes Business braucht Profi-Logistik –
              <br />
              <span className="text-gradient-primary">kein eigenes Lager.</span>
              <AnimatedUnderline isVisible={servicesUnderline.isVisible} className="mx-auto max-w-[200px]" />
            </h2>
            <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
              Du sparst Lagerfläche, Packzeit und operative Fehler – während deine Bestellungen zuverlässig rausgehen. Unsere Services greifen genau dort ein, wo Fulfillment dich heute ausbremst: Versand, Retouren, Pre-FBA, Aktionsversand und Verpackung.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {services.map((service, index) => (
              <ServiceCard
                key={service.title}
                imageUrl={service.imageUrl}
                title={service.title}
                description={service.description}
                href={service.href}
                index={index}
                featured={false}
                imageAlt={service.imageAlt}
                direction={index % 2 === 0 ? "left" : "right"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Unsere Entwicklung – Growth Chart */}
      <section className="py-12 sm:py-16 lg:py-24 bg-background">
        <div className="container-section">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <h2 ref={growthUnderline.ref} className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 sm:mb-8">
              Unsere{" "}
              <span className="text-gradient-primary">Entwicklung</span>
              <AnimatedUnderline isVisible={growthUnderline.isVisible} className="mx-auto max-w-[160px]" />
            </h2>
            <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
              {growthSummary
                ? `Von ${growthSummary.start.toLocaleString("de-DE")} Paketen zu ${growthSummary.highest.toLocaleString("de-DE")} Paketen. So sind wir gewachsen.`
                : "So sind wir gewachsen."}
            </p>
          </div>

          <div className="glass-card rounded-2xl p-4 sm:p-8">
            <GrowthChart onSummaryChange={setGrowthSummary} />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-background">
        <div className="container-section">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <h2 className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 sm:mb-8">
              Das sagen unsere Kunden
            </h2>
            <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
              Echte Stimmen von echten Händlern, die mit emoose wachsen.
            </p>
          </div>

          <LazySection
            factory={() => import("@/components/home/TestimonialCarousel")}
            props={{}}
            rootMargin="400px 0px"
            fallback={<SectionFallback />}
          />
        </div>
      </section>


      {/* 9 Gründe Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-section">
        <div className="container-section">
          <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12">
            <h2 ref={reasonsUnderline.ref} className="font-display text-2xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 sm:mb-8">
              Warum Fulfillment dein{" "}
              <span className="text-gradient-primary">Leben leichter macht</span>
              <AnimatedUnderline isVisible={reasonsUnderline.isVisible} className="mx-auto max-w-[180px]" />
            </h2>
            <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
              Weniger Stress, mehr Freiheit – 9 Gründe, die für sich sprechen.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                icon: Clock,
                title: "Zeit ist Geld",
                description: "Feierabend um 18 Uhr – statt bis Mitternacht Pakete zu packen.",
              },
              {
                icon: PiggyBank,
                title: "Geringere Logistikkosten",
                description: "Keine Lagerhalle mieten. Kein Personal einstellen. Nur zahlen, was du wirklich brauchst.",
              },
              {
                icon: TrendingUp,
                title: "Umsatzwachstum",
                description: "Deine Konkurrenz liefert schneller? Jetzt nicht mehr.",
              },
              {
                icon: Award as any,
                title: "Hohe Kundenzufriedenheit",
                description: "Deine Kunden schreiben 5-Sterne-Reviews – ohne dass du einen Finger rührst.",
              },
              {
                icon: Heart,
                title: "Hohe Wiederkäuferquoten",
                description: "Wenn's beim ersten Mal perfekt läuft, kommen sie wieder. Garantiert.",
              },
              {
                icon: TreePine,
                title: "Nachhaltigkeit",
                description: "Grüner versenden, besser schlafen.",
              },
              {
                icon: AlertTriangle,
                title: "Geringere Retourenquoten",
                description: "Weniger Fehler = weniger Retouren = mehr Gewinn. So einfach ist das.",
              },
              {
                icon: Globe,
                title: "Internationalisierung",
                description: "Deine Produkte in ganz Europa – ohne dass du ein Wort Französisch sprichst.",
              },
              {
                icon: Target,
                title: "Fokus aufs Kerngeschäft",
                description: "Du baust deine Marke. Wir kümmern uns um den Rest.",
              },
            ].map((reason) => {
              const Icon = reason.icon;
              return (
                <div key={reason.title} className="glass-card p-6 sm:p-8 rounded-xl flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-foreground mb-1.5">
                      {reason.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Einwände kurz beantwortet */}
      <section className="py-10 sm:py-14 bg-background">
        <div className="container-section">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-6 sm:mb-8">
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3">
                Noch unsicher? Kurz beantwortet.
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Die wichtigsten Punkte, bevor du dein Fulfillment auslagerst.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              {[
                "Keine Mindestmengen",
                "Kostenloses Setup",
                "25+ Shopsysteme",
                "Transparente Preise",
                "Persönliche Betreuung",
              ].map((answer) => (
                <div key={answer} className="glass-card rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm sm:text-base font-medium text-foreground leading-snug">
                    {answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA nach 9 Gründen */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-section">
        <div className="container-section text-center">
          <p className="text-xl lg:text-2xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Überzeugend? Dann lass uns gemeinsam starten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/anfrage">
              <Button variant="hero" size="xl" className="w-full sm:w-auto">
                Anfrage starten
              </Button>
            </Link>
            <Link to="/preise">
              <Button variant="heroOutline" size="xl" className="w-full sm:w-auto border-foreground text-foreground hover:bg-foreground hover:text-background">
                Preise ansehen
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <LatestBlogPosts />
    </Layout>
  );
}